
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Named Label Chip Component
 *
 * @file NamedLabelChip.jsx
 * @author Murhsid Ahamedh
 */

import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import CustomCard from './CustomCard';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  content: {
    marginTop: 12
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }
});

const CustomAccordion = (props) => {
  const {
    title,
    right,
    children,
    defaultExpanded,
    onToggle,
    style,
    className,
    headerStyle,
    contentStyle,
    backgroundColor,
    cardStyle,
    caretColor
  } = props;

  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(Boolean(defaultExpanded));

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (onToggle) onToggle(next);
  };

  const arrowStyle = {
    color: caretColor || '#5A3FD4',
    fontSize: 24
  };

  return (
    <CustomCard backgroundColor={backgroundColor} style={{ padding: 12, borderRadius: 12, ...style, ...(cardStyle || {}) }} className={className}>
      <Box
        role="button"
        tabIndex={0}
        onClick={handleToggle}
        onKeyPress={(e) => { if (e.key === 'Enter') handleToggle(); }}
        className={classes.header}
        style={headerStyle}
      >
        <Box>{title}</Box>
        <Box className={classes.rightSection}>
          {right}
          {expanded ? <KeyboardArrowUpIcon style={arrowStyle} /> : <KeyboardArrowDownIcon style={arrowStyle} />}
        </Box>
      </Box>

      {expanded ? (
        <Box className={classes.content} style={contentStyle}>
          {children}
        </Box>
      ) : null}
    </CustomCard>
  );
};

CustomAccordion.propTypes = {
  title: PropTypes.node.isRequired,
  right: PropTypes.node,
  children: PropTypes.node,
  defaultExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  style: PropTypes.object,
  className: PropTypes.string,
  headerStyle: PropTypes.object,
  contentStyle: PropTypes.object,
  backgroundColor: PropTypes.string,
  cardStyle: PropTypes.object,
  caretColor: PropTypes.string
};

CustomAccordion.defaultProps = {
  right: null,
  children: null,
  defaultExpanded: false,
  onToggle: undefined,
  style: {},
  className: '',
  headerStyle: {},
  contentStyle: {},
  backgroundColor: '',
  cardStyle: undefined,
  caretColor: undefined
};

export default CustomAccordion;

